import { getI18n } from "react-i18next";
import { cleanNotifications, showNotification } from "@mantine/notifications";
import * as Sentry from "@sentry/react";
import { GraphQLRequestError } from "src/graphql/utils/utils";
import { TranslationKeys } from "src/types/i18next";
import { scrubSensitive } from "src/utils/scrub";

interface ErrorInfo {
    name: string;
    message: string;
    errorKind?: string;
}

export const extractErrorInfo = (error: unknown): ErrorInfo => {
    if (typeof error === "string") {
        return { name: "Unknown error", message: error, errorKind: "unknown" };
    }

    let errorKind: string | undefined;

    if (error instanceof GraphQLRequestError) {
        errorKind = "GraphQLRequestError";
    }

    if (error instanceof Error) {
        return { name: error.name, message: error.message, errorKind: errorKind || "Error" };
    }

    try {
        return { name: "Unknown error", message: JSON.stringify(scrubSensitive(error)), errorKind: "unknown" };
    } catch {
        return { name: "Unknown error", message: "Unknown error", errorKind: "unknown" };
    }
};

type Context = Record<string, string | number | boolean | null | undefined>;

interface Translation {
    key: TranslationKeys;
    namespace: string;
    params?: Record<string, string | number | boolean | null | undefined>;
}

interface Meta {
    title?: Translation;
    message?: Translation;
    additionalMessage?: string;
    requestId?: string | null;
    context?: Context;
}

export const reportError = (error: unknown, meta: Meta) => {
    const { name, message, errorKind } = extractErrorInfo(error);
    const errorName = meta.title ? `${meta.title.namespace}:${meta.title.key}` : name;
    const errorMessage = meta.message ? `${meta.message.namespace}:${meta.message.key}` : message;
    let requestId: string | undefined | null = meta.requestId;

    Sentry.withScope((scope) => {
        scope.setTag("errorSource", "handledError");
        scope.setTag("errorName", errorName);
        scope.setTag("errorKind", errorKind);
        scope.setExtra("errorMessage", errorMessage);

        if (meta?.additionalMessage) {
            scope.setExtra("additionalMessage", meta.additionalMessage);
        }

        if (error instanceof GraphQLRequestError) {
            scope.setContext(
                "graphql",
                scrubSensitive({
                    operationType: error.operationType,
                    operationName: error.operationName,
                    normalizedErrors: error.errorsNormalized.map((err) => err.message).join(", "),
                    variablesKeys: error.variablesKeys,
                })
            );

            requestId = error.requestId;
            if (error.fingerprint?.length) {
                scope.setFingerprint(error.fingerprint as string[]);
            }
        }

        if (requestId) {
            scope.setExtra("requestId", requestId);
        }

        const context = scrubSensitive(meta?.context);
        if (context && typeof context === "object") {
            for (const [key, value] of Object.entries(context)) {
                scope.setExtra(key, value as unknown);
            }
        }

        Sentry.captureException(error);
    });
};

export const handleError = (
    error: unknown,
    options?: Partial<Meta> & {
        silent?: boolean;
        autoClose?: boolean;
    }
) => {
    const { t } = getI18n();
    const {
        silent,
        autoClose = true,
        message = { key: "error_message", namespace: "common" },
        title = { key: "error", namespace: "common" },
        additionalMessage,
        ...restMeta
    } = options ?? {};
    const messageText =
        t(message.key, { ns: message.namespace, ...message.params }) +
        (additionalMessage ? `: ${additionalMessage}` : "");
    const titleText = t(title.key, { ns: title.namespace, ...title.params });

    reportError(error, { title, message, additionalMessage, ...restMeta });

    if (!silent) {
        cleanNotifications();
        showNotification({ color: "red", title: titleText, message: messageText, autoClose });
    }
    return error;
};
